import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
} from '@dnd-kit/sortable';
import {
    loadGroceryV2Items,
    loadGroceryV2Categories,
    createGroceryV2Item,
    createGroceryV2Category,
    updateGroceryV2Item,
    deleteGroceryV2Item,
    deleteGroceryV2Category,
    updateMultipleGroceryV2Items,
    updateMultipleGroceryV2Categories,
    checkGroceryV2Item,
    deleteCheckedGroceryV2Items,
    loadGroceryV2Favorites,
    updateGroceryV2UndoList,
    removeGroceryV2UndoList,
    updateGroceryV2UndoBatch
} from '../../redux-components/actions/groceryV2Actions';
import Loading from '../../components/Layout/Loading';
import Layout from '../../components/Layout/Layout';
import GroceryV2Header from '../../components/GroceryV2/GroceryV2Header';
import GroceryV2List from '../../components/GroceryV2/GroceryV2List';
import GroceryV2FavoritesList from '../../components/GroceryV2/GroceryV2FavoritesList';
import EditButton from "../../components/Layout/EditButton";
import useRefetchOnFocus from "../../hooks/useRefetchOnFocus";

const GroceryV2 = () => {
    const history = useNavigate();
    const dispatch = useDispatch();

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    useEffect(() => {
        if(!userInfo) {
            history('/login?next=/grocery');
        }
    }, [userInfo, history])

    const editMode = useSelector((state) => state.editMode);
    const groceryV2ItemGet = useSelector(state => state.groceryV2ItemGet);
    const { loading: itemsLoading, groceryV2Items = [] } = groceryV2ItemGet;
    const groceryV2CategoryGet = useSelector(state => state.groceryV2CategoryGet);
    const { loading: categoriesLoading, groceryV2Categories = [] } = groceryV2CategoryGet;
    const groceryV2ItemCreate = useSelector(state => state.groceryV2ItemCreate);
    const { success: createSuccess, groceryV2Item: createdItem } = groceryV2ItemCreate;
    const groceryV2UndoList = useSelector(state => state.groceryV2UndoList);
    const { undoList } = groceryV2UndoList;

    // Local state
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [newItem, setNewItem] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [overId, setOverId] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);

    // Load data. Keyed on the id, not the object: refreshUser() merges a fresh userInfo into the store
    // on every page load, so the object identity always changes even when nothing about the user did —
    // which re-ran these three fetches and made the list load twice on mount.
    const userId = userInfo?._id;
    useEffect(() => {
        if (userId) {
            dispatch(loadGroceryV2Items());
            dispatch(loadGroceryV2Categories());
            dispatch(loadGroceryV2Favorites());
        }
    }, [dispatch, userId]);

    // Fetch the latest when the tab comes back to the foreground.
    // Silent: the user didn't ask for this, so no spinner — the list just updates if anything changed.
    useRefetchOnFocus(() => {
        dispatch(loadGroceryV2Items({ background: true }));
        dispatch(loadGroceryV2Categories({ background: true }));
        dispatch(loadGroceryV2Favorites({ background: true }));
    }, Boolean(userInfo));

    // Update local state when Redux state changes
    useEffect(() => {
        if (groceryV2Items) {
            setItems(groceryV2Items.map(item => ({
                id: item._id,
                text: item.text,
                category: item.category?._id || null,
                position: item.position,
                isChecked: item.isChecked || false
            })));
        }
    }, [groceryV2Items]);

    useEffect(() => {
        if (groceryV2Categories) {
            setCategories(groceryV2Categories.map(cat => ({
                id: cat._id,
                name: cat.name,
                position: cat.position
            })));
        }
    }, [groceryV2Categories]);

    // Handle successful item creation (like from favorites)
    useEffect(() => {
        if (createSuccess && createdItem) {
            const newItem = {
                id: createdItem._id,
                text: createdItem.text,
                category: createdItem.category?._id || null,
                position: createdItem.position,
                isChecked: createdItem.isChecked || false
            };
            
            setItems(prev => {
                // Replace any temporary item with the same text, or add if it doesn't exist
                const tempItemIndex = prev.findIndex(item => 
                    item.id.startsWith('temp_') && item.text === newItem.text
                );
                
                if (tempItemIndex >= 0) {
                    // Replace temporary item with real item
                    const updated = [...prev];
                    updated[tempItemIndex] = newItem;
                    return updated;
                } else if (!prev.some(item => item.id === newItem.id)) {
                    // Add new item if it doesn't exist
                    return [...prev, newItem];
                }
                
                return prev;
            });
        }
    }, [createSuccess, createdItem]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 300, 
                tolerance: 8,
                distance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 300, 
                tolerance: 8,
                distance: 5,
            },
        })
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.sortable-item')) {
                setSelectedItems([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDragStart = (event) => {
        const { active } = event;
        setActiveId(active.id);
        if (!selectedItems.includes(active.id)) {
            setSelectedItems([active.id]);
        }
        // Prevent page scrolling during drag on mobile
        document.body.classList.add('dragging');
    };

    const handleDragOver = (event) => {
        const { active, over } = event;
        if (over) {
            const overIsCategory = over.id.startsWith('cat') || over.id === 'uncategorized';
            const activeIsCategory = active.id.startsWith('cat');

            if (!overIsCategory && !activeIsCategory) {
                const activeIndex = items.findIndex(item => item.id === active.id);
                const overIndex = items.findIndex(item => item.id === over.id);
                setOverId({
                    id: over.id,
                    position: activeIndex > overIndex ? 'top' : 'bottom'
                });
            } else {
                setOverId({ id: over.id, position: 'bottom' });
            }
        } else {
            setOverId(null);
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const isCategory = active.id.startsWith('cat') || categories.some(cat => cat.id === active.id);

            if (isCategory) {
                const oldIndex = categories.findIndex((cat) => cat.id === active.id);
                const newIndex = categories.findIndex((cat) => cat.id === over.id);
                const newCategories = arrayMove(categories, oldIndex, newIndex);
                
                // Update positions in database
                const categoryUpdates = newCategories.map((cat, index) => ({
                    id: cat.id,
                    position: index
                }));
                
                await dispatch(updateMultipleGroceryV2Categories(categoryUpdates));
                await dispatch(loadGroceryV2Categories());
            } else {
                const movingItems = selectedItems.map(id => items.find(item => item.id === id));
                const targetItem = items.find(item => item.id === over.id);
                
                if (over.id === 'uncategorized' || categories.some(cat => cat.id === over.id)) {
                    // Moving items to a different category container (not specific position)
                    const newCategory = over.id === 'uncategorized' ? null : over.id;
                    
                    // Optimistic update
                    setItems(prev => prev.map(item => 
                        selectedItems.includes(item.id) ? { ...item, category: newCategory } : item
                    ));
                    
                    try {
                        const itemUpdates = movingItems.map(item => ({
                            id: item.id,
                            category: newCategory
                        }));
                        
                        await dispatch(updateMultipleGroceryV2Items(itemUpdates));
                    } catch (error) {
                        // Revert optimistic update on error
                        setItems(prev => prev.map(item => {
                            const originalItem = movingItems.find(mi => mi.id === item.id);
                            return originalItem ? { ...item, category: originalItem.category } : item;
                        }));
                    }
                } else if (targetItem) {
                    // Dropping on a specific item (could be same or different category)
                    const targetCategory = targetItem.category;
                    
                    // Filter out the moving items to get the base array
                    const otherItems = items.filter(item => !selectedItems.includes(item.id));
                    
                    // Separate items by category
                    const uncategorizedItems = otherItems.filter(item => !item.category);
                    const categorizedItems = categories.reduce((acc, cat) => {
                        acc[cat.id] = otherItems.filter(item => item.category === cat.id);
                        return acc;
                    }, {});
                    
                    // Find insertion point in the target category
                    let targetCategoryItems = targetCategory ? categorizedItems[targetCategory] || [] : uncategorizedItems;
                    const baseInsertIndex = targetCategoryItems.findIndex(item => item.id === over.id);
                    
                    // Adjust insert index based on drop position (top or bottom)
                    const insertIndex = overId?.position === 'bottom' ? baseInsertIndex + 1 : baseInsertIndex;
                    
                    // Update moving items to new category
                    const updatedMovingItems = movingItems.map(item => ({
                        ...item,
                        category: targetCategory
                    }));
                    
                    // Insert moving items at the correct position
                    const newTargetCategoryItems = [
                        ...targetCategoryItems.slice(0, insertIndex),
                        ...updatedMovingItems,
                        ...targetCategoryItems.slice(insertIndex)
                    ];
                    
                    // Rebuild the complete items array
                    let newItemOrder = [];
                    
                    // Add uncategorized items
                    if (!targetCategory) {
                        newItemOrder.push(...newTargetCategoryItems);
                    } else {
                        newItemOrder.push(...uncategorizedItems);
                    }
                    
                    // Add categorized items
                    categories.forEach(cat => {
                        if (targetCategory === cat.id) {
                            newItemOrder.push(...newTargetCategoryItems);
                        } else {
                            newItemOrder.push(...(categorizedItems[cat.id] || []));
                        }
                    });
                    
                    // Optimistic update
                    setItems(newItemOrder);
                    
                    // Update both category and positions
                    const itemUpdates = newItemOrder.map((item, index) => ({
                        id: item.id,
                        category: item.category,
                        position: index
                    }));
                    
                    try {
                        await dispatch(updateMultipleGroceryV2Items(itemUpdates));
                    } catch (error) {
                        // Revert optimistic update on error
                        dispatch(loadGroceryV2Items());
                    }
                }
            }
        }

        setActiveId(null);
        setOverId(null);
        setSelectedItems([]);
        // Re-enable page scrolling after drag
        document.body.classList.remove('dragging');
    };

    const handleDragCancel = () => {
        setActiveId(null);
        setOverId(null);
        setSelectedItems([]);
        // Re-enable page scrolling if drag is cancelled
        document.body.classList.remove('dragging');
    };

    const handleAddItem = async () => {
        if (newItem.trim() !== '') {
            // Optimistic update
            const tempId = `temp_${Date.now()}`;
            const newItemObj = { id: tempId, text: newItem, category: null, position: items.length, isChecked: false };
            setItems(prev => [...prev, newItemObj]);
            setNewItem('');

            try {
                await dispatch(createGroceryV2Item(newItem));
                // The useEffect listening to createSuccess will handle the UI update
            } catch (error) {
                // Revert optimistic update on error
                setItems(prev => prev.filter(item => item.id !== tempId));
                setNewItem(newItem); // Restore the input
            }
        }
    };

    const handleRemoveItem = async (id) => {
        // Save to undo list before deletion
        const removedItem = items.find(item => item.id === id);
        if (removedItem) {
            dispatch(updateGroceryV2UndoList(removedItem));
        }

        // Optimistic update
        setItems(prev => prev.filter(item => item.id !== id));
        setSelectedItems(prev => prev.filter(itemId => itemId !== id));

        try {
            await dispatch(deleteGroceryV2Item(id));
        } catch (error) {
            // Revert optimistic update on error
            if (removedItem) {
                setItems(prev => [...prev, removedItem].sort((a, b) => a.position - b.position));
            }
        }
    };

    const handleEditItem = async (id, newText) => {
        // Optimistic update
        const oldItem = items.find(item => item.id === id);
        setItems(prev => prev.map(item => item.id === id ? { ...item, text: newText } : item));

        try {
            await dispatch(updateGroceryV2Item(id, newText));
        } catch (error) {
            // Revert optimistic update on error
            if (oldItem) {
                setItems(prev => prev.map(item => item.id === id ? oldItem : item));
            }
        }
    };

    const handleAddCategory = async () => {
        if (newCategory.trim() !== '') {
            // Optimistic update
            const tempId = `temp_cat_${Date.now()}`;
            const newCategoryObj = { id: tempId, name: newCategory, position: categories.length };
            setCategories(prev => [...prev, newCategoryObj]);
            setNewCategory('');

            try {
                await dispatch(createGroceryV2Category(newCategory));
                // Background refresh without loading state
                setTimeout(() => {
                    dispatch(loadGroceryV2Categories());
                }, 100);
            } catch (error) {
                // Revert optimistic update on error
                setCategories(prev => prev.filter(cat => cat.id !== tempId));
                setNewCategory(newCategory); // Restore the input
            }
        }
    };

    const handleRemoveCategory = async (id) => {
        // Save to undo list before deletion
        const removedCategory = categories.find(cat => cat.id === id);
        if (removedCategory) {
            dispatch(updateGroceryV2UndoList(removedCategory));
        }

        // Optimistic update
        setCategories(prev => prev.filter(cat => cat.id !== id));
        // Move items from deleted category to uncategorized
        setItems(prev => prev.map(item => item.category === id ? { ...item, category: null } : item));

        try {
            await dispatch(deleteGroceryV2Category(id));
            // Background refresh without loading state
            setTimeout(() => {
                dispatch(loadGroceryV2Categories());
                dispatch(loadGroceryV2Items());
            }, 100);
        } catch (error) {
            // Revert optimistic update on error
            if (removedCategory) {
                setCategories(prev => [...prev, removedCategory].sort((a, b) => a.position - b.position));
            }
        }
    };

    const handleItemSelect = (id) => {
        setSelectedItems(prev =>
            prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
        );
    };

    const handleCheckItem = async (id) => {
        // Optimistic update
        const item = items.find(item => item.id === id);
        if (item) {
            const newCheckedState = !item.isChecked;
            setItems(prev => prev.map(item => item.id === id ? { ...item, isChecked: newCheckedState } : item));

            try {
                await dispatch(checkGroceryV2Item(id, item.isChecked));
            } catch (error) {
                // Revert optimistic update on error
                setItems(prev => prev.map(item => item.id === id ? { ...item, isChecked: !newCheckedState } : item));
            }
        }
    };

    const handleDeleteCheckedItems = async () => {
        // Save items to undo list before deleting
        const checkedItems = items.filter(item => item.isChecked);
        if (checkedItems.length > 0) {
            dispatch(updateGroceryV2UndoBatch(checkedItems));
        }

        // Optimistic update
        setItems(prev => prev.filter(item => !item.isChecked));

        try {
            await dispatch(deleteCheckedGroceryV2Items());
        } catch (error) {
            // Revert optimistic update on error
            setItems(prev => [...prev, ...checkedItems].sort((a, b) => a.position - b.position));
        }
    };

    const handleRefresh = async () => {
        dispatch(loadGroceryV2Items());
        dispatch(loadGroceryV2Categories());
        dispatch(loadGroceryV2Favorites());
    };

    const handleUndo = () => {
        if (undoList.length > 0) {
            const lastAction = undoList[undoList.length - 1];

            if (lastAction.type === 'batch') {
                // Restore all items and categories in the batch
                lastAction.items?.forEach(item => {
                    dispatch(createGroceryV2Item(item.text, item.category));
                });
                
                lastAction.categories?.forEach(category => {
                    dispatch(createGroceryV2Category(category.name));
                });
            } else {
                // Restore single item
                dispatch(createGroceryV2Item(lastAction.text, lastAction.category));
            }

            // Remove from undo list
            dispatch(removeGroceryV2UndoList());

            // Refresh after a short delay to ensure server sync
            setTimeout(() => {
                dispatch(loadGroceryV2Items());
                dispatch(loadGroceryV2Categories());
            }, 500);
        }
    };

    // Only show loading on initial load, not during updates
    if ((itemsLoading || categoriesLoading) && items.length === 0 && categories.length === 0) {
        return <Loading />;
    }

    return (
        <>
        <Layout title={"Grocery List"} col>
            <GroceryV2Header
                newItem={newItem}
                setNewItem={setNewItem}
                handleAddItem={handleAddItem}
                newCategory={newCategory}
                setNewCategory={setNewCategory}
                handleAddCategory={handleAddCategory}
                handleDeleteCheckedItems={handleDeleteCheckedItems}
                checkedItemsCount={items.filter(item => item.isChecked).length}
                editMode={editMode}
                handleRefresh={handleRefresh}
                handleUndo={handleUndo}
                undoListLength={undoList?.length || 0}
            />
            
            <div className="mb-4 w-full md:w-2/3 mx-auto">
                <GroceryV2FavoritesList />
            </div>
            
            <GroceryV2List
                sensors={sensors}
                items={items}
                categories={categories}
                selectedItems={selectedItems}
                overId={overId}
                activeId={activeId}
                handleDragStart={handleDragStart}
                handleDragOver={handleDragOver}
                handleDragEnd={handleDragEnd}
                handleDragCancel={handleDragCancel}
                handleRemoveItem={handleRemoveItem}
                handleEditItem={handleEditItem}
                handleCheckItem={handleCheckItem}
                handleItemSelect={handleItemSelect}
                handleRemoveCategory={handleRemoveCategory}
                editMode={editMode}
            />
            
        </Layout>
        <div className='block fixed right-5 bottom-5 z-50 text-white'>
            <EditButton/>
        </div>
        </>
    );
};

export default GroceryV2;
